import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import { z } from 'zod';
import { createApiResponseSchema } from '../../../../utils';
import { partnerApiPath } from '../../../apiPath';
import { partnerHttpClient } from '../../../httpClient';

const ProposalCampaignStatusSchema = z
  .enum(['ACTIVE', 'INACTIVE'])
  .describe('ProposalCampaignStatusSchema');

export type ProposalCampaignStatus = z.infer<typeof ProposalCampaignStatusSchema>;

const GetProposalCampaignsRequestSchema = z
  .object({
    platform: z.string(),
    title: z.string().nullable().optional(),
    brandIds: z.array(z.string()).nullable().optional(),
    status: ProposalCampaignStatusSchema.nullable().optional(),
    page: z.number().int().min(1).optional(),
    size: z.number().int().min(1).max(100).optional(),
  })
  .describe('GetProposalCampaignsRequestSchema');

export type GetProposalCampaignsRequest = z.infer<typeof GetProposalCampaignsRequestSchema>;

const ProposalProgressCountSchema = z
  .object({
    proposalSentCount: z.number(),
    proposalAcceptedCount: z.number(),
    completedCount: z.number(),
  })
  .describe('ProposalProgressCountSchema');

const ProposalCampaignSchema = z
  .object({
    campaignId: z.number(),
    status: ProposalCampaignStatusSchema,
    goodsImage: z.string(),
    brandName: z.string(),
    brandId: z.string(),
    campaignTitle: z.string(),
    createdAt: z.string(),
    progressCount: ProposalProgressCountSchema,
  })
  .describe('ProposalCampaignSchema');

export type ProposalCampaign = z.infer<typeof ProposalCampaignSchema>;

const GetProposalCampaignsResponseDataSchema = z
  .object({
    list: z.array(ProposalCampaignSchema),
  })
  .describe('GetProposalCampaignsResponseDataSchema');

const GetProposalCampaignsResponseSchema = createApiResponseSchema(
  GetProposalCampaignsResponseDataSchema,
);

export type GetProposalCampaignsResponse = z.infer<typeof GetProposalCampaignsResponseSchema>;

export const getProposalCampaigns = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetProposalCampaignsRequestSchema),
    async (params: GetProposalCampaignsRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.get<GetProposalCampaignsResponse>(
          partnerApiPath.getProposalCampaigns,
          params,
        ),
      ),
    validateApiResponse(GetProposalCampaignsResponseSchema),
  ),
);
